import React, { useState, useEffect } from 'react';
import { X, Save, ArrowLeft, Package, Tag, Layers, Percent, CreditCard, FileText, CheckCircle2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { getSession } from '../../getSession';
import {
    getProductUnitList,
    getProductCategoryList,
    getProductTaxList,
    getProductAccountList,
    getProductCustomFields,
    saveProduct,
    getTagListWithFilter
} from '../../utils/lead';
import Swal from 'sweetalert2';
import MediaLibrary from '../MediaLibrary';

const AddProductForm = ({ onClose, onSuccess, initialName = '', source = 'Quotation' }) => {
    const { userId } = getSession();
    const [isLoading, setIsLoading] = useState(false);
    const [units, setUnits] = useState([]);
    const [categories, setCategories] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [customFields, setCustomFields] = useState([]);

    const [formData, setFormData] = useState({
        ProductID: '0',
        ProductName: initialName,
        SKU: '',
        UnitID: '0',
        IsGoods: '1', // '1' for Goods, '0' for Services
        IsServices: '0',
        HSNCode: '',
        SACCode: '',
        IsDigitalService: '0',
        IsTaxable: '1',
        HomeStateTaxIDs: '',
        OtherTaxIDs: '',
        IsSaleable: '1',
        SalesPrice: '',
        SalesAccount: '0',
        CastPrice: '',
        CastAccount: '0',
        DecimalRateQuantity: '0',
        Description: '',
        CategoryID: '0',
        IsPublished: '1',
        IsFeatured: '0',
        IsActive: '1',
        LiveProductsURL: '',
        ThumbnailUrl: '',
        CustomFieldValueJson: '[]',
        VariantsJson: '',
        VCombinationJson: '',
        ProductTagsIDs: ''
    });

    const [customFieldValues, setCustomFieldValues] = useState({});
    const [isVariantsEnabled, setIsVariantsEnabled] = useState(false);
    const [variants, setVariants] = useState([]); // [{ name: '', values: [] }]
    const [combinations, setCombinations] = useState([]); // [{ variant1: 'val', variant2: 'val', price: '', imagePath: '' }]
    const [isCombinationsFinalized, setIsCombinationsFinalized] = useState(false);
    const [productImages, setProductImages] = useState([]);
    const [mediaModal, setMediaModal] = useState({ show: false, context: null, index: null }); // context: 'thumbnail' | 'product_image' | 'variant_image'

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [unitList, catList, taxList, accList, cfList] = await Promise.all([
                    getProductUnitList({ strUserID: userId }),
                    getProductCategoryList({ strUserID: userId }),
                    getProductTaxList({ strUserID: userId }),
                    getProductAccountList({ strUserID: userId }),
                    getProductCustomFields({ UserId: userId })
                ]);

                setUnits(Array.isArray(unitList) ? unitList : []);
                setCategories(Array.isArray(catList) ? catList : []);
                setTaxes(Array.isArray(taxList) ? taxList : []);
                setAccounts(Array.isArray(accList) ? accList : []);
                setCustomFields(Array.isArray(cfList) ? cfList : []);

                // Initialize custom field values
                const initialCfValues = {};
                cfList?.forEach(cf => {
                    initialCfValues[cf.FieldName] = cf.DefaultValue || '';
                });
                setCustomFieldValues(initialCfValues);
            } catch (err) {
                console.error('Error fetching product support data:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [userId]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? '1' : '0') : value
        }));
    };

    const handleRadioChange = (name, value) => {
        if (name === 'type') {
            setFormData(prev => ({
                ...prev,
                IsGoods: value === 'goods' ? '1' : '0',
                IsServices: value === 'service' ? '1' : '0'
            }));
        } else if (name === 'taxable') {
            setFormData(prev => ({
                ...prev,
                IsTaxable: value === 'taxable' ? '1' : '0'
            }));
        }
    };

    const handleCustomFieldChange = (name, value) => {
        setCustomFieldValues(prev => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        if (!formData.ProductName.trim()) return 'Please provide product name';
        if (!formData.SKU.trim()) return 'Please provide SKU';
        if (formData.UnitID === '0') return 'Please select unit';
        if (formData.IsGoods === '1' && !formData.HSNCode.trim()) return 'Please provide HSN Code';
        if (formData.IsServices === '1' && !formData.SACCode.trim()) return 'Please provide SAC Code';
        if (formData.IsSaleable === '1') {
            if (!formData.SalesPrice || isNaN(formData.SalesPrice)) return 'Please provide a valid sales price';
            if (!formData.CastPrice || isNaN(formData.CastPrice)) return 'Please provide a valid cost price';
        }
        if (formData.CategoryID === '0') return 'Please select product category';

        // Custom field validation (mandatory check)
        for (const cf of customFields) {
            if (cf.IsMan && !customFieldValues[cf.FieldName]?.trim()) {
                return `Please provide ${cf.FieldName}`;
            }
        }

        return null;
    };

    const addVariant = () => {
        setVariants([...variants, { name: '', values: [] }]);
    };

    const removeVariant = (index) => {
        const newVariants = variants.filter((_, i) => i !== index);
        setVariants(newVariants);
        if (isCombinationsFinalized) {
            setIsCombinationsFinalized(false);
            setCombinations([]);
        }
    };

    const updateVariant = (index, field, value) => {
        const newVariants = [...variants];
        if (field === 'values') {
            // This is now handled by handleVariantKeyDown or tag removal
            return;
        } else {
            newVariants[index][field] = value;
        }
        setVariants(newVariants);
    };

    const handleVariantKeyDown = (e, vIdx) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const val = e.target.value.trim();
            if (val) {
                const newVariants = [...variants];
                if (!newVariants[vIdx].values.includes(val)) {
                    newVariants[vIdx].values.push(val);
                }
                setVariants(newVariants);
                e.target.value = '';
            }
        }
    };

    const removeVariantValue = (vIdx, valIdx) => {
        const newVariants = [...variants];
        newVariants[vIdx].values = newVariants[vIdx].values.filter((_, i) => i !== valIdx);
        setVariants(newVariants);
    };

    const openMediaLibrary = (context, index = null) => {
        setMediaModal({ show: true, context, index });
    };

    const handleSelectMedia = (media) => {
        const url = media.url || media.thumbnail;
        if (mediaModal.context === 'thumbnail') {
            setFormData({ ...formData, ThumbnailUrl: url });
        } else if (mediaModal.context === 'product_image') {
            if (!productImages.includes(url)) {
                setProductImages([...productImages, url]);
            }
        } else if (mediaModal.context === 'variant_image') {
            updateCombination(mediaModal.index, 'imagePath', url);
        }
        setMediaModal({ show: false, context: null, index: null });
    };

    const removeProductImage = (url) => {
        setProductImages(productImages.filter(img => img !== url));
    };

    const generateCombinations = () => {
        const validVariants = variants.filter(v => v.name && v.values.length > 0);
        if (validVariants.length === 0) {
            Swal.fire({ icon: 'warning', title: 'Invalid Variants', text: 'Please add at least one variant with values.' });
            return;
        }

        const cartesian = (...a) => a.reduce((a, b) => a.flatMap(d => b.map(e => [d, e].flat())));
        const variantValues = validVariants.map(v => v.values);
        let allCombinations = [];

        if (variantValues.length === 1) {
            allCombinations = variantValues[0].map(v => [v]);
        } else {
            allCombinations = cartesian(...variantValues);
        }

        if (allCombinations.length > 200) {
            Swal.fire({ icon: 'warning', title: 'Too many combinations', text: 'Combinations exceed 200. Please reduce values.' });
            return;
        }

        const newCombinations = allCombinations.map(combo => {
            const comboObj = {};
            validVariants.forEach((v, idx) => {
                comboObj[v.name] = combo[idx];
            });
            comboObj.price = formData.SalesPrice || '';
            comboObj.imagePath = '';
            return comboObj;
        });

        setCombinations(newCombinations);
        setIsCombinationsFinalized(true);
    };

    const updateCombination = (index, field, value) => {
        const newCombos = [...combinations];
        newCombos[index][field] = value;
        setCombinations(newCombos);
    };

    const removeCombination = (index) => {
        setCombinations(combinations.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        const error = validate();
        if (error) {
            Swal.fire({ icon: 'warning', title: 'Validation Error', text: error, confirmButtonColor: '#2a9629' });
            return;
        }

        setIsLoading(true);
        try {
            // Prepare CustomFieldValueJson
            const cfValuesArray = customFields.map(cf => ({
                lbltxt: cf.FieldName,
                value: customFieldValues[cf.FieldName]
            }));

            const payload = {
                productDetailsToSave_Obj: {
                    ...formData,
                    CustomFieldValueJson: JSON.stringify(cfValuesArray),
                    VariantsJson: isVariantsEnabled ? JSON.stringify(variants.filter(v => v.name && v.values.length > 0)) : '',
                    VCombinationJson: isVariantsEnabled ? JSON.stringify(combinations) : '',
                    ThumbnailUrl: formData.ThumbnailUrl,
                    LiveProductsURL: productImages.join(',')
                },
                strUserID: userId
            };

            const response = await saveProduct(payload);
            if (response && (response.Status?.toLowerCase() === 'success' || response.status?.toLowerCase() === 'success')) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: response.Message || 'Product saved successfully!',
                    timer: 1500,
                    showConfirmButton: false
                });
                if (onSuccess) onSuccess(response.product || response.Product || { ...formData, Id: response.ProductID || response.ProductId || response.Id });
                onClose();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.Message || 'Failed to save product'
                });
            }
        } catch (err) {
            console.error('Submit error:', err);
            Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred while saving the product.' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 999999 }}>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} />

            <div className="relative w-full max-w-2xl bg-gray-50 h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
                {/* Header */}
                <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">New Product</h2>
                            <p className="text-xs text-[#2a9629] font-medium flex items-center gap-1">
                                Back to Add {source}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-red-50 hover:text-red-500 rounded-full transition-colors text-gray-400">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                            <Package className="w-4 h-4 text-blue-500" />
                            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Basic Information</span>
                        </div>
                        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Product Name <span className="text-red-500">*</span></label>
                                <input
                                    name="ProductName"
                                    value={formData.ProductName}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                                    placeholder="Enter product name"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">SKU <span className="text-red-500">*</span></label>
                                <input
                                    name="SKU"
                                    value={formData.SKU}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                                    placeholder="Stock Keeping Unit"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Unit <span className="text-red-500">*</span></label>
                                <select
                                    name="UnitID"
                                    value={formData.UnitID}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                                >
                                    <option value="0">Select Unit</option>
                                    {units.map(u => <option key={u.UnitID} value={u.UnitID}>{u.UnitName} ({u.UnitDisplay})</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Tax & Type */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                            <Tag className="w-4 h-4 text-purple-500" />
                            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Classification & Tax</span>
                        </div>
                        <div className="p-5 space-y-5">
                            <div className="flex gap-6">
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${formData.IsGoods === '1' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                                        {formData.IsGoods === '1' && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                                    </div>
                                    <input type="radio" className="hidden" checked={formData.IsGoods === '1'} onChange={() => handleRadioChange('type', 'goods')} />
                                    <span className={`text-sm font-medium ${formData.IsGoods === '1' ? 'text-blue-700' : 'text-gray-600'}`}>Goods</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${formData.IsServices === '1' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                                        {formData.IsServices === '1' && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                                    </div>
                                    <input type="radio" className="hidden" checked={formData.IsServices === '1'} onChange={() => handleRadioChange('type', 'service')} />
                                    <span className={`text-sm font-medium ${formData.IsServices === '1' ? 'text-blue-700' : 'text-gray-600'}`}>Service</span>
                                </label>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {formData.IsGoods === '1' ? (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">HSN Code <span className="text-red-500">*</span></label>
                                        <input
                                            name="HSNCode"
                                            value={formData.HSNCode}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                            placeholder="Harmonized System Nomenclature"
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">SAC Code <span className="text-red-500">*</span></label>
                                        <input
                                            name="SACCode"
                                            value={formData.SACCode}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                            placeholder="Service Accounting Code"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Category <span className="text-red-500">*</span></label>
                                    <select
                                        name="CategoryID"
                                        value={formData.CategoryID}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                                    >
                                        <option value="0">Select Category</option>
                                        {categories.map(c => <option key={c.CategoryID} value={c.CategoryID}>{c.CategoryName}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="pt-2 border-t border-gray-100">
                                <label className="block text-xs font-bold text-gray-500 mb-3 uppercase">Tax Information</label>
                                <div className="flex gap-6 mb-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" checked={formData.IsTaxable === '1'} onChange={() => handleRadioChange('taxable', 'taxable')} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                                        <span className="text-sm text-gray-700">Taxable</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" checked={formData.IsTaxable === '0'} onChange={() => handleRadioChange('taxable', 'non-taxable')} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                                        <span className="text-sm text-gray-700">Non-Taxable</span>
                                    </label>
                                </div>

                                {formData.IsTaxable === '1' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in duration-300">
                                        <div>
                                            <label className="block text-[10px] font-bold text-gray-400 mb-1 uppercase">Home State Taxes</label>
                                            <select
                                                name="HomeStateTaxIDs"
                                                value={formData.HomeStateTaxIDs}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none"
                                            >
                                                <option value="">Select Tax</option>
                                                {taxes.map(t => <option key={t.ID} value={t.ID}>{t.Name} ({t.Rate}%)</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-gray-400 mb-1 uppercase">Other State Taxes</label>
                                            <select
                                                name="OtherTaxIDs"
                                                value={formData.OtherTaxIDs}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs outline-none"
                                            >
                                                <option value="">Select Tax</option>
                                                {taxes.map(t => <option key={t.ID} value={t.ID}>{t.Name} ({t.Rate}%)</option>)}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pricing */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-green-500" />
                            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Pricing & Accounts</span>
                        </div>
                        <div className="p-5 space-y-4">
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                                <input
                                    type="checkbox"
                                    name="IsSaleable"
                                    checked={formData.IsSaleable === '1'}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 text-[#2a9629] rounded focus:ring-[#2a9629] border-gray-300"
                                />
                                <span className="text-sm font-medium text-gray-700">Is product saleable?</span>
                            </label>

                            {formData.IsSaleable === '1' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-300">
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Sales Price <span className="text-red-500">*</span></label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                                                <input
                                                    name="SalesPrice"
                                                    value={formData.SalesPrice}
                                                    onChange={handleInputChange}
                                                    className="w-full pl-8 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-green-500 outline-none"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Sales Account</label>
                                            <select
                                                name="SalesAccount"
                                                value={formData.SalesAccount}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none"
                                            >
                                                <option value="0">Select Account</option>
                                                {accounts.map(a => <option key={a.Id || a.ID} value={a.Id || a.ID}>{a.Name || a.Text}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Cost Price <span className="text-red-500">*</span></label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                                                <input
                                                    name="CastPrice"
                                                    value={formData.CastPrice}
                                                    onChange={handleInputChange}
                                                    className="w-full pl-8 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-green-500 outline-none"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Cost Account</label>
                                            <select
                                                name="CastAccount"
                                                value={formData.CastAccount}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none"
                                            >
                                                <option value="0">Select Account</option>
                                                {accounts.map(a => <option key={a.Id || a.ID} value={a.Id || a.ID}>{a.Name || a.Text}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Variants Management */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                            <Layers className="w-4 h-4 text-indigo-500" />
                            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Product Variants</span>
                        </div>
                        <div className="p-5">
                            <label className="flex items-center gap-2 cursor-pointer group mb-4">
                                <input
                                    type="checkbox"
                                    checked={isVariantsEnabled}
                                    onChange={(e) => {
                                        setIsVariantsEnabled(e.target.checked);
                                        if (e.target.checked && variants.length === 0) {
                                            setVariants([{ name: '', values: [] }]);
                                        }
                                    }}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm font-bold text-gray-700">This product has variants</span>
                            </label>

                            {isVariantsEnabled && (
                                <div className="space-y-4 animate-in fade-in duration-300">
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Define Variants</span>
                                            {!isCombinationsFinalized && (
                                                <button onClick={addVariant} className="text-[10px] font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 bg-white px-2 py-1 rounded border border-blue-100 shadow-sm transition-all">
                                                    <Plus className="w-3 h-3" /> Add Variant
                                                </button>
                                            )}
                                        </div>

                                        <div className="space-y-3">
                                            {variants.map((variant, vIdx) => (
                                                <div key={vIdx} className="grid grid-cols-12 gap-3 items-start">
                                                    <div className="col-span-4">
                                                        <input
                                                            disabled={isCombinationsFinalized}
                                                            placeholder="Variant Name (e.g. Color)"
                                                            value={variant.name}
                                                            onChange={(e) => updateVariant(vIdx, 'name', e.target.value)}
                                                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-gray-100"
                                                        />
                                                    </div>
                                                    <div className="col-span-7">
                                                        <div className="flex flex-wrap gap-1.5 p-2 bg-white border border-gray-200 rounded-lg min-h-[38px] focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all">
                                                            {variant.values.map((val, valIdx) => (
                                                                <span key={valIdx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded border border-blue-100 group/tag">
                                                                    {val}
                                                                    {!isCombinationsFinalized && (
                                                                        <X
                                                                            onClick={() => removeVariantValue(vIdx, valIdx)}
                                                                            className="w-3 h-3 cursor-pointer hover:text-red-500 transition-colors"
                                                                        />
                                                                    )}
                                                                </span>
                                                            ))}
                                                            {!isCombinationsFinalized && (
                                                                <input
                                                                    placeholder={variant.values.length === 0 ? "Type and press Enter..." : ""}
                                                                    onKeyDown={(e) => handleVariantKeyDown(e, vIdx)}
                                                                    className="flex-1 min-w-[60px] bg-transparent outline-none text-xs text-gray-700"
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-span-1 flex justify-center pt-1">
                                                        {!isCombinationsFinalized && (
                                                            <button onClick={() => removeVariant(vIdx)} className="p-1 text-gray-300 hover:text-red-500 transition-colors">
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-4 flex gap-3">
                                            <button
                                                onClick={() => {
                                                    if (isCombinationsFinalized) {
                                                        setIsCombinationsFinalized(false);
                                                        setCombinations([]);
                                                    } else {
                                                        generateCombinations();
                                                    }
                                                }}
                                                className={`px-5 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${isCombinationsFinalized ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-indigo-600 text-white shadow-lg shadow-indigo-100 hover:bg-indigo-700'}`}
                                            >
                                                {isCombinationsFinalized ? 'Reset Combination' : 'Create Combination Grid'}
                                            </button>
                                        </div>
                                    </div>

                                    {isCombinationsFinalized && combinations.length > 0 && (
                                        <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm overflow-x-auto">
                                            <table className="w-full text-[11px]">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        {variants.filter(v => v.name).map((v, idx) => (
                                                            <th key={idx} className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">{v.name}</th>
                                                        ))}
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Price</th>
                                                        <th className="px-4 py-3 text-left font-bold text-gray-500 uppercase tracking-tight">Image</th>
                                                        <th className="px-4 py-3 text-center font-bold text-gray-500 uppercase tracking-tight w-12">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {combinations.map((combo, cIdx) => (
                                                        <tr key={cIdx} className="hover:bg-gray-50/50 transition-colors group">
                                                            {variants.filter(v => v.name).map((v, idx) => (
                                                                <td key={idx} className="px-4 py-3 font-semibold text-gray-800">{combo[v.name]}</td>
                                                            ))}
                                                            <td className="px-4 py-3">
                                                                <div className="relative w-28">
                                                                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 font-medium">₹</span>
                                                                    <input
                                                                        type="number"
                                                                        value={combo.price}
                                                                        onChange={(e) => updateCombination(cIdx, 'price', e.target.value)}
                                                                        className="w-full pl-6 pr-2 py-1.5 bg-gray-50 border border-gray-200 rounded focus:border-indigo-400 outline-none transition-all"
                                                                        placeholder="Price"
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center gap-2">
                                                                    {combo.imagePath ? (
                                                                        <div className="relative group/img">
                                                                            <img src={combo.imagePath} className="w-8 h-8 rounded border-2 border-white shadow-sm object-cover" alt="Variant" />
                                                                            <button
                                                                                onClick={() => updateCombination(cIdx, 'imagePath', '')}
                                                                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover/img:opacity-100 transition-opacity shadow-sm"
                                                                            >
                                                                                <X className="w-2 h-2" />
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => openMediaLibrary('variant_image', cIdx)}
                                                                            className="p-2 bg-gray-50 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all border border-gray-100"
                                                                        >
                                                                            <FileText className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                <button onClick={() => removeCombination(cIdx)} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-all">
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Media & Assets Section */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                            <Plus className="w-4 h-4 text-pink-500" />
                            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Media & Assets</span>
                        </div>
                        <div className="p-5 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Thumbnail Upload */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">Product Thumbnail</label>
                                    <div
                                        onClick={() => openMediaLibrary('thumbnail')}
                                        className={`relative w-40 h-40 rounded-2xl border-2 border-dashed transition-all flex flex-col items-center justify-center cursor-pointer overflow-hidden group ${formData.ThumbnailUrl ? 'border-blue-200 bg-white' : 'border-gray-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/30'}`}
                                    >
                                        {formData.ThumbnailUrl ? (
                                            <>
                                                <img src={formData.ThumbnailUrl} className="w-full h-full object-cover" alt="Thumbnail" />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <span className="text-white text-[10px] font-bold px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full border border-white/30">Change Image</span>
                                                </div>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, ThumbnailUrl: '' }); }}
                                                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-lg transform scale-0 group-hover:scale-100 transition-transform"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                                    <Plus className="w-6 h-6 text-blue-600" />
                                                </div>
                                                <span className="text-[10px] font-bold text-gray-400 uppercase">Select Thumbnail</span>
                                            </>
                                        )}
                                    </div>
                                    <p className="mt-2 text-[10px] text-gray-400 italic">Recommended: 800x800px, max 2MB</p>
                                </div>

                                {/* Multi-Image Gallery */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">Product Gallery</label>
                                    <div className="flex flex-wrap gap-3">
                                        {productImages.map((img, idx) => (
                                            <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden group border border-gray-100 shadow-sm">
                                                <img src={img} className="w-full h-full object-cover" alt={`Product ${idx}`} />
                                                <button
                                                    onClick={() => removeProductImage(img)}
                                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <X className="w-2.5 h-2.5" />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            onClick={() => openMediaLibrary('product_image')}
                                            className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center bg-gray-50 hover:border-blue-400 hover:bg-blue-50 transition-all text-gray-400 hover:text-blue-600"
                                        >
                                            <Plus className="w-5 h-5 mb-1" />
                                            <span className="text-[8px] font-bold uppercase">Add</span>
                                        </button>
                                    </div>
                                    <p className="mt-2 text-[10px] text-gray-400 italic">Add multiple angles of your product.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Description & Custom Fields */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-orange-500" />
                            <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Additional Details</span>
                        </div>
                        <div className="p-5 space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">Product Description</label>
                                <textarea
                                    name="Description"
                                    value={formData.Description}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none min-h-[100px] resize-none"
                                    placeholder="Enter detailed description..."
                                />
                            </div>

                            {customFields.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-50">
                                    {customFields.map((cf, idx) => (
                                        <div key={idx}>
                                            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase">
                                                {cf.FieldName} {cf.IsMan && <span className="text-red-500">*</span>}
                                            </label>
                                            {cf.FieldType === 'text' || cf.FieldType === 'numeric' ? (
                                                <input
                                                    type={cf.FieldType === 'numeric' ? 'number' : 'text'}
                                                    value={customFieldValues[cf.FieldName] || ''}
                                                    onChange={(e) => handleCustomFieldChange(cf.FieldName, e.target.value)}
                                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
                                                />
                                            ) : cf.FieldType === 'select' ? (
                                                <select
                                                    value={customFieldValues[cf.FieldName] || ''}
                                                    onChange={(e) => handleCustomFieldChange(cf.FieldName, e.target.value)}
                                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
                                                >
                                                    <option value="">Select Option</option>
                                                    {cf.Choices?.split(',').map(choice => (
                                                        <option key={choice} value={choice}>{choice}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={customFieldValues[cf.FieldName] || ''}
                                                    onChange={(e) => handleCustomFieldChange(cf.FieldName, e.target.value)}
                                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500"
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Settings / Flags */}
                    <div className="flex flex-wrap gap-6 px-2">
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input type="checkbox" name="IsPublished" checked={formData.IsPublished === '1'} onChange={handleInputChange} className="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500" />
                            <span className="text-xs font-bold text-gray-600 uppercase">Published</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input type="checkbox" name="IsFeatured" checked={formData.IsFeatured === '1'} onChange={handleInputChange} className="w-4 h-4 rounded text-yellow-600 border-gray-300 focus:ring-yellow-500" />
                            <span className="text-xs font-bold text-gray-600 uppercase">Featured</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer group">
                            <input type="checkbox" name="IsActive" checked={formData.IsActive === '1'} onChange={handleInputChange} className="w-4 h-4 rounded text-green-600 border-gray-300 focus:ring-green-500" />
                            <span className="text-xs font-bold text-gray-600 uppercase">Active</span>
                        </label>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-white border-t px-6 py-4 flex items-center justify-between shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-lg transition-all"
                    >
                        Close
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="px-8 py-2.5 bg-[#2a9629] hover:bg-[#238022] text-white rounded-lg text-sm font-bold flex items-center gap-2 shadow-lg shadow-green-200 disabled:opacity-70 transition-all"
                    >
                        {isLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        Save & Apply
                    </button>
                </div>
            </div>

            {mediaModal.show && (
                <MediaLibrary
                    show={mediaModal.show}
                    onclose={() => setMediaModal({ show: false, context: null, index: null })}
                    onSelectMedia={handleSelectMedia}
                    filterOption="Images"
                />
            )}
        </div>
    );
};

export default AddProductForm;
